import React, { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaEnvelope, FaEye, FaEyeSlash, FaLock } from "react-icons/fa";
import AuthPageLogo from "../../components/AuthPageLogo";
import FounderAccessHeroPanel from "../../components/auth/FounderAccessHeroPanel";
import SuperAdminAuthService from "../../services/SuperAdminAuthService";
import SuperAdminStorageService from "../../services/SuperAdminStorageService";
import {
  clearSuperAdminPostLoginRedirect,
  resolveSuperAdminPostLoginRedirect,
} from "../../utils/superAdminAuthRedirect";
import "../../assets/styles/login-page.scss";

export default function SuperAdminLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  if (SuperAdminStorageService.isAuthenticated()) {
    return <Navigate to="/super-admin/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await SuperAdminAuthService.login(email, password);
      if (!result.token) {
        toast.error(result.message || "Login failed");
        return;
      }
      SuperAdminStorageService.setAuth(
        { token: result.token, admin: result.admin },
        { remember, cookieDays: 8 },
      );
      toast.success(result.message || "Welcome, Super Admin");
      const redirectTo = resolveSuperAdminPostLoginRedirect(location);
      clearSuperAdminPostLoginRedirect();
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Super Admin login failed";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page login-page--split login-page--super-admin">
      <div className="login-page__shell login-page__shell--split">
        <FounderAccessHeroPanel />

        <main className="login-page__form-panel login-page__form-panel--split login-page__form-panel--super-admin">
          <div className="login-page__form-inner">
            <div className="login-page__form-logo">
              <AuthPageLogo />
            </div>
            <h2 className="login-page__form-title">Super Admin</h2>
            <p className="login-page__form-subtitle">Sign in to the control centre</p>

            <form onSubmit={handleSubmit} noValidate>
              <div className="login-page__field">
                <label className="login-page__label" htmlFor="super-admin-email">
                  Email or username
                </label>
                <div className="login-page__input-wrap">
                  <FaEnvelope className="login-page__input-icon" aria-hidden />
                  <input
                    id="super-admin-email"
                    type="text"
                    className="login-page__input login-page__input--tinted"
                    placeholder="Enter your email or username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="username"
                  />
                </div>
              </div>

              <div className="login-page__field">
                <label className="login-page__label" htmlFor="super-admin-password">
                  Password
                </label>
                <div className="login-page__input-wrap">
                  <FaLock className="login-page__input-icon" aria-hidden />
                  <input
                    id="super-admin-password"
                    type={showPassword ? "text" : "password"}
                    className="login-page__input login-page__input--tinted"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="login-page__input-toggle"
                    onClick={() => setShowPassword((open) => !open)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <div className="login-page__options">
                <label className="login-page__remember" htmlFor="super-admin-remember">
                  <input
                    type="checkbox"
                    id="super-admin-remember"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                  />
                  Remember me
                </label>
                <Link to="/login" className="login-page__forgot">
                  User login
                </Link>
              </div>

              <button type="submit" className="login-page__submit" disabled={loading}>
                <FaLock aria-hidden />
                {loading ? "Signing in..." : "Sign in"}
              </button>
            </form>

            <p className="login-page__signup">
              Super Admin accounts cannot sign in via the{" "}
              <Link to="/login">standard login page</Link>.
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
